/**
 * Stable per-question ids: `key` (uuid) survives renames, the label does not. Element paths, condition
 * targets and answer lookups use `key ?? label`, so a form saved before keys keeps the label path.
 */
import type { Condition, Field, FormResponses } from '@/types/templates'
import { fieldKindOf } from '@/utils/form_fields/field_kind'

export const labelPath = (label: string | undefined): string => (label ?? '').replaceAll('.', '')

export function newFieldKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** The element path (and answer key) of a question. */
export function fieldPath(field: Pick<Field, 'key' | 'label'>): string {
  return field.key || labelPath(field.label)
}

/** A copy of `field` as a new question: fresh key, everything else as in the original. */
export function duplicateField(field: Field, label: string): Field {
  // Open: the copy exists to be renamed, and a collapsed card would hide the one input the user came for.
  return { ...field, key: newFieldKey(), label, expanded: true }
}

const sameQuestion = (a: Field, b: Field): boolean => labelPath(a.label) === labelPath(b.label) && fieldKindOf(a) === fieldKindOf(b)

/**
 * The live field a condition targets: the legacy copy's own key, then `field_key` while the copy is
 * empty or still matches it, then a unique label. Same order as `FormFieldKeyServices._resolve_target`.
 */
export function conditionTargetField(condition: Condition, fields: Field[]): Field | undefined {
  const legacy = condition.field && typeof condition.field === 'object' && Object.keys(condition.field).length > 0 ? condition.field : undefined
  const byKey = (key?: string) => (key ? fields.find((f) => f.key === key) : undefined)
  const fromCopy = byKey(legacy?.key)
  if (fromCopy) return fromCopy
  const fromFieldKey = byKey(condition.field_key)
  if (fromFieldKey && (!legacy || sameQuestion(legacy, fromFieldKey))) return fromFieldKey
  if (!legacy) return undefined
  const byLabel = fields.filter((f) => sameQuestion(legacy, f))
  return byLabel.length === 1 ? byLabel[0] : undefined
}

/** The path a condition points at; the legacy label path when no live field resolves. */
export function resolveConditionTarget(condition: Condition, fields: Field[]): string {
  const target = conditionTargetField(condition, fields)
  return target ? fieldPath(target) : labelPath(typeof condition.field === 'object' ? condition.field.label : undefined)
}

/**
 * Re-maps a flat answers model onto the paths of `schemaFields`: each answered field is matched to a
 * schema field by key, then by label. Answers already on a schema path stay where they are.
 */
export function rekeyAnswersToSchema(model: FormResponses, responseFields: Field[], schemaFields: Field[]): FormResponses {
  const out: FormResponses = { ...model }
  const taken = new Set<Field>()
  for (const answered of responseFields) {
    const from = fieldPath(answered)
    if (out[from] === undefined) continue
    const target =
      schemaFields.find((s) => !taken.has(s) && answered.key && s.key === answered.key) ??
      schemaFields.find((s) => !taken.has(s) && labelPath(s.label) === labelPath(answered.label))
    if (!target) continue
    taken.add(target)
    const to = fieldPath(target)
    if (to === from) continue
    if (out[to] === undefined) out[to] = out[from]
    delete out[from]
  }
  return out
}
